import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Users, Shuffle, Loader2, Zap, MessageSquareReply } from 'lucide-react';
import { Card } from '../../../components/ui/card';
import { whatsappAPI } from '../whatsappAPI';

const PERFIS = [
  ['supervisor', 'Supervisor', 'Vê e assume qualquer conversa, transfere de qualquer pessoa, vê a auditoria.'],
  ['atendente', 'Atendente', 'Vê todas as conversas, puxa da fila e atende as suas (ou responde todas, se ligado abaixo). É o padrão.'],
  ['operador', 'Operador', 'Só vê e responde as conversas que recebe (distribuição ou transferência).'],
];
const DOT = { online: 'bg-emerald-500', ausente: 'bg-amber-400', ocupado: 'bg-red-500' };
const errMsg = (e, p) => e?.response?.data?.detail || p;

export default function EquipeConfig({ cfg, up, admin }) {
  const [lista, setLista] = useState(null);
  const [salvando, setSalvando] = useState(null);
  const [distribuindo, setDistribuindo] = useState(false);
  const carregar = () => whatsappAPI.equipe().then(r => setLista(r.data || [])).catch(() => setLista([]));
  useEffect(() => { carregar(); }, []);

  const salvar = async (u, dados) => {
    setSalvando(u.id);
    setLista(l => l.map(x => (x.id === u.id ? { ...x, ...dados } : x)));
    try { await whatsappAPI.equipeSalvar(u.id, dados); carregar(); }
    catch (e) { toast.error(errMsg(e, 'Não foi possível salvar')); carregar(); }
    finally { setSalvando(null); }
  };
  const distribuirAgora = async () => {
    setDistribuindo(true);
    try { const { data } = await whatsappAPI.distribuirAgora(); toast.success(data.distribuidas ? `${data.distribuidas} conversa(s) distribuída(s)` : 'Ninguém disponível para receber agora'); carregar(); }
    catch (e) { toast.error(errMsg(e, 'Não foi possível distribuir')); }
    finally { setDistribuindo(false); }
  };

  return (
    <div className="space-y-4" data-testid="wa-config-equipe">
      <Card className="p-5">
        <h3 className="font-semibold flex items-center gap-2"><MessageSquareReply className="w-5 h-5 text-emerald-600" /> Quem responde o quê</h3>
        <label className="flex items-start gap-2 text-sm cursor-pointer mt-3">
          <input type="checkbox" checked={!!cfg.atendente_responde_todas} onChange={e => up('atendente_responde_todas')(e.target.checked)} disabled={!admin}
            className="w-4 h-4 mt-0.5 accent-emerald-600" data-testid="wa-cfg-responde-todas" />
          <span>
            <span className="font-medium block">Atendentes veem e respondem todas as conversas</span>
            <span className="text-xs text-muted-foreground">
              Quem tem o perfil <b>Atendente</b> pode escrever também nas conversas que estão com outra pessoa (como o supervisor), sem tomar a conversa
              para si. Desligado: precisa pedir a transferência. Operadores continuam vendo só as deles.
            </span>
          </span>
        </label>
      </Card>

      <Card className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><Shuffle className="w-5 h-5 text-emerald-600" /> Distribuição automática</h3>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
              Quando alguém entra na fila, o sistema entrega a conversa para um atendente disponível. Se o setor da conversa tiver membros,
              só eles recebem. Também distribui quando alguém libera uma vaga ou fica online.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer shrink-0">
            <input type="checkbox" checked={!!cfg.distribuicao_ativa} onChange={e => up('distribuicao_ativa')(e.target.checked)} disabled={!admin}
              className="w-4 h-4 accent-emerald-600" data-testid="wa-dist-ativa" /> Ativa
          </label>
        </div>
        <fieldset disabled={!admin} className={`mt-4 grid md:grid-cols-3 gap-4 ${cfg.distribuicao_ativa ? '' : 'opacity-50'}`}>
          <div>
            <p className="text-sm font-medium mb-1.5">Quem recebe a próxima</p>
            <div className="flex flex-col gap-1.5 text-sm">
              {[['menor_carga', 'Quem tem menos conversas abertas'], ['rodizio', 'Rodízio (um de cada vez)']].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="modo" checked={cfg.distribuicao_modo === k} onChange={() => up('distribuicao_modo')(k)} className="accent-emerald-600" data-testid={`wa-dist-modo-${k}`} />{l}
                </label>
              ))}
            </div>
          </div>
          <label className="block text-sm font-medium">Limite de conversas por pessoa
            <input type="number" min={0} max={100} value={cfg.limite_conversas ?? 5} onChange={e => up('limite_conversas')(Number(e.target.value))}
              className="mt-1.5 w-24 h-9 px-3 rounded-lg border bg-background text-sm block" data-testid="wa-dist-limite" />
            <span className="text-xs font-normal text-muted-foreground">0 = sem limite. Cada pessoa pode ter o seu na tabela abaixo.</span>
          </label>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!cfg.distribuicao_so_online} onChange={e => up('distribuicao_so_online')(e.target.checked)} className="w-4 h-4 mt-0.5 accent-emerald-600" />
            <span><span className="font-medium block">Só para quem está online</span><span className="text-xs text-muted-foreground">Ausente, ocupado ou com o sistema fechado não recebe.</span></span>
          </label>
        </fieldset>
        <p className="text-xs text-muted-foreground mt-3">Depois de mudar estas opções, clique em <b>Salvar alterações</b> no topo.</p>
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between gap-2 mb-1">
          <h3 className="font-semibold flex items-center gap-2"><Users className="w-5 h-5 text-emerald-600" /> Equipe</h3>
          {cfg.distribuicao_ativa && (
            <button onClick={distribuirAgora} disabled={distribuindo} className="h-8 px-3 rounded-lg border text-sm flex items-center gap-1.5 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50" data-testid="wa-dist-agora">
              {distribuindo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Distribuir a fila agora
            </button>
          )}
        </div>
        <div className="grid sm:grid-cols-3 gap-2 mb-4">
          {PERFIS.map(([k, l, d]) => (
            <div key={k} className="text-xs text-muted-foreground"><b className="text-foreground">{l}:</b> {d}</div>
          ))}
        </div>
        {!lista ? <div className="py-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 text-left">
                <tr className="border-b border-slate-100 dark:border-slate-800">
                  <th className="font-medium py-2">Pessoa</th><th className="font-medium py-2">Perfil</th>
                  <th className="font-medium py-2">Recebe automático</th><th className="font-medium py-2">Limite</th>
                  <th className="font-medium py-2">Setores</th><th className="font-medium py-2 text-right">Abertas agora</th>
                </tr>
              </thead>
              <tbody>
                {lista.map(u => (
                  <tr key={u.id} className="border-b border-slate-100 dark:border-slate-800 last:border-0" data-testid={`wa-equipe-${u.id}`}>
                    <td className="py-2.5">
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${DOT[u.presenca] || 'bg-slate-300'}`} title={u.presenca} />
                        <span className="font-medium">{u.nome}</span>
                        {salvando === u.id && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
                      </span>
                    </td>
                    <td className="py-2.5">
                      {u.perfil_fixo ? <span className="text-slate-500">Administrador</span> : (
                        <select value={u.perfil} disabled={!admin} onChange={e => salvar(u, { perfil: e.target.value })} data-testid={`wa-equipe-perfil-${u.id}`}
                          className="h-8 px-2 rounded-lg border bg-background text-sm">
                          {PERFIS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                      )}
                    </td>
                    <td className="py-2.5">
                      <input type="checkbox" checked={!!u.recebe_automatico} disabled={!admin} onChange={e => salvar(u, { recebe_automatico: e.target.checked })}
                        className="w-4 h-4 accent-emerald-600" aria-label={`${u.nome} recebe automático`} data-testid={`wa-equipe-recebe-${u.id}`} />
                    </td>
                    <td className="py-2.5">
                      <select value={u.max_conversas == null ? '' : String(u.max_conversas)} disabled={!admin}
                        onChange={e => salvar(u, { max_conversas: e.target.value === '' ? null : Number(e.target.value) })}
                        className="h-8 px-2 rounded-lg border bg-background text-sm">
                        <option value="">Geral ({cfg.limite_conversas || 'sem limite'})</option>
                        {[1, 2, 3, 4, 5, 6, 8, 10, 15, 20].map(n => <option key={n} value={n}>{n}</option>)}
                        <option value="0">Sem limite</option>
                      </select>
                    </td>
                    <td className="py-2.5 text-xs text-slate-500">{u.setores.join(', ') || '—'}</td>
                    <td className="py-2.5 text-right tabular-nums">{u.carga}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
